import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingCart, TrendingDown, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import type { TokenInfo } from "../hooks/useSquadToken";
import { useSquadToken } from "../hooks/useSquadToken";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatMon(wei: bigint, decimals = 6): string {
  if (wei === 0n) return "0";
  const val = Number(wei) / 1e18;
  return val.toLocaleString(undefined, { maximumFractionDigits: decimals });
}

function formatTokens(wei: bigint): string {
  const val = Number(wei) / 1e18;
  if (val >= 1_000_000) return (val / 1_000_000).toFixed(2) + "M";
  if (val >= 1_000) return (val / 1_000).toFixed(2) + "K";
  return val.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function parseMonInput(input: string): bigint {
  try {
    const n = parseFloat(input);
    if (isNaN(n) || n <= 0) return 0n;
    return BigInt(Math.floor(n * 1e18));
  } catch {
    return 0n;
  }
}

function parseTokenInput(input: string): bigint {
  try {
    const n = parseFloat(input);
    if (isNaN(n) || n <= 0) return 0n;
    return BigInt(Math.floor(n * 1e18));
  } catch {
    return 0n;
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface BuyTokenModalProps {
  token: TokenInfo;
  walletAddress?: string;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function BuyTokenModal({ token, walletAddress, onClose }: BuyTokenModalProps) {
  const { estimateBuy, estimateSell, buyTokens, sellTokens, getUserBalance, getAvailableSupply, loading } = useSquadToken();

  const [tab, setTab] = useState<"buy" | "sell">("buy");

  // Buy state
  const [monInput, setMonInput] = useState("");
  const [estimatedTokens, setEstimatedTokens] = useState<bigint>(0n);
  const [estimatingBuy, setEstimatingBuy] = useState(false);

  // Sell state
  const [tokenInput, setTokenInput] = useState("");
  const [estimatedMon, setEstimatedMon] = useState<bigint>(0n);
  const [estimatingSell, setEstimatingSell] = useState(false);
  const [userBalance, setUserBalance] = useState<bigint>(0n);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Shared state
  const [txHash, setTxHash] = useState<string | null>(null);
  const [txError, setTxError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const availableSupply = getAvailableSupply(token);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Fetch user balance when sell tab opens or wallet changes
  useEffect(() => {
    if (tab !== "sell" || !walletAddress) return;
    setLoadingBalance(true);
    getUserBalance(token.tokenContract, walletAddress)
      .then(setUserBalance)
      .catch(() => setUserBalance(0n))
      .finally(() => setLoadingBalance(false));
  }, [tab, walletAddress, token.tokenContract, getUserBalance]);

  // Debounced buy estimate
  useEffect(() => {
    if (tab !== "buy") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const wei = parseMonInput(monInput);
    if (wei === 0n) { setEstimatedTokens(0n); return; }
    debounceRef.current = setTimeout(async () => {
      setEstimatingBuy(true);
      try {
        const est = await estimateBuy(token.tokenContract, wei);
        setEstimatedTokens(est);
      } catch {
        setEstimatedTokens(0n);
      } finally {
        setEstimatingBuy(false);
      }
    }, 400);
  }, [monInput, tab, token.tokenContract, estimateBuy]);

  // Debounced sell estimate
  useEffect(() => {
    if (tab !== "sell") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const wei = parseTokenInput(tokenInput);
    if (wei === 0n) { setEstimatedMon(0n); return; }
    debounceRef.current = setTimeout(async () => {
      setEstimatingSell(true);
      try {
        const est = await estimateSell(token.tokenContract, wei);
        setEstimatedMon(est);
      } catch {
        setEstimatedMon(0n);
      } finally {
        setEstimatingSell(false);
      }
    }, 400);
  }, [tokenInput, tab, token.tokenContract, estimateSell]);

  const handleBuy = useCallback(async () => {
    setTxError(null);
    const wei = parseMonInput(monInput);
    if (wei === 0n) { setTxError("Enter a valid MON amount."); return; }
    try {
      const hash = await buyTokens(token.tokenContract, wei);
      setTxHash(hash);
    } catch (e: unknown) {
      setTxError(e instanceof Error ? e.message : "Transaction failed.");
    }
  }, [monInput, token.tokenContract, buyTokens]);

  const handleSell = useCallback(async () => {
    setTxError(null);
    const wei = parseTokenInput(tokenInput);
    if (wei === 0n) { setTxError("Enter a valid token amount."); return; }
    if (wei > userBalance) { setTxError("Amount exceeds your balance."); return; }
    try {
      const hash = await sellTokens(token.tokenContract, wei);
      setTxHash(hash);
    } catch (e: unknown) {
      setTxError(e instanceof Error ? e.message : "Transaction failed.");
    }
  }, [tokenInput, token.tokenContract, sellTokens, userBalance]);

  const handleTabChange = (newTab: "buy" | "sell") => {
    setTab(newTab);
    setTxError(null);
    setTxHash(null);
  };

  const priceWei = token.currentPrice ?? 0n;

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        {/* Modal */}
        <motion.div
          key="modal"
          initial={{ opacity: 0, scale: 0.92, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 16 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          className="card-glass glow-border-strong rounded-2xl p-6 w-full max-w-md relative"
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-[--color-pink] to-purple-600">
              {tab === "buy"
                ? <ShoppingCart size={18} className="text-white" />
                : <TrendingDown size={18} className="text-white" />
              }
            </div>
            <div>
              <h2 className="font-display text-xl text-white tracking-wide">
                {tab === "buy" ? "Buy" : "Sell"} {token.symbol}
              </h2>
              <p className="text-xs text-[--color-pink]/80 font-mono">{token.creatorDomain}</p>
            </div>
          </div>

          {/* Tab Toggle */}
          {!txHash && (
            <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-5">
              <button
                onClick={() => handleTabChange("buy")}
                className={`flex-1 py-2 rounded-lg text-xs font-display tracking-wide transition-all ${
                  tab === "buy"
                    ? "bg-[--color-pink] text-white shadow"
                    : "text-white/50 hover:text-white"
                }`}
              >
                Buy
              </button>
              <button
                onClick={() => handleTabChange("sell")}
                className={`flex-1 py-2 rounded-lg text-xs font-display tracking-wide transition-all ${
                  tab === "sell"
                    ? "bg-[--color-pink] text-white shadow"
                    : "text-white/50 hover:text-white"
                }`}
              >
                Sell
              </button>
            </div>
          )}

          {txHash ? (
            /* ── Success State ── */
            <div className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="font-display text-lg text-white mb-1">
                {tab === "buy" ? "Purchase Sent!" : "Sale Sent!"}
              </p>
              <p className="text-xs text-white/50 font-body mb-4">
                Your transaction is being confirmed on Monad.
              </p>
              <div className="bg-white/5 rounded-xl p-3 mb-4">
                <p className="text-[10px] text-white/40 mb-1 font-body">Transaction Hash</p>
                <p className="font-mono text-xs text-[--color-pink] break-all">{txHash}</p>
              </div>
              <button onClick={onClose} className="btn-pink w-full py-2.5">
                Done
              </button>
            </div>
          ) : tab === "buy" ? (
            /* ── Buy Flow ── */
            <>
              {/* Current Price */}
              <div className="bg-white/5 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-white/50 font-body">Current Price</span>
                  <span className="font-mono text-sm text-[--color-pink] font-bold">
                    {formatMon(priceWei, 9)} MON / {token.symbol}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-white/50 font-body">Available Supply</span>
                  <span className="font-mono text-xs text-white/80">
                    {formatTokens(availableSupply)} {token.symbol}
                  </span>
                </div>
              </div>

              {/* MON Input */}
              <div className="mb-4">
                <label className="text-xs text-white/50 font-body mb-1.5 block">
                  Amount to spend (MON)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.0"
                    value={monInput}
                    onChange={(e) => setMonInput(e.target.value)}
                    className="w-full bg-white/5 border border-[--color-border] rounded-xl px-4 py-3 font-mono text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[--color-pink]/60 transition-colors"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40 font-body">
                    MON
                  </span>
                </div>
                <div className="flex gap-2 mt-2">
                  {["0.1", "0.5", "1", "5"].map((v) => (
                    <button
                      key={v}
                      onClick={() => setMonInput(v)}
                      className="flex-1 text-xs py-1 rounded-lg bg-white/5 border border-[--color-border] text-white/60 hover:text-[--color-pink] hover:border-[--color-pink]/50 transition-all font-mono"
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Estimate */}
              <div className="bg-white/5 rounded-xl p-3 mb-5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-white/50 font-body">You receive (est.)</span>
                  <div className="flex items-center gap-1.5">
                    {estimatingBuy && <Loader2 size={12} className="text-[--color-pink] animate-spin" />}
                    <span className="font-mono text-sm text-white font-semibold">
                      {estimatedTokens > 0n ? formatTokens(estimatedTokens) : "—"}{" "}
                      <span className="text-[--color-pink]">{token.symbol}</span>
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-white/30 font-body mt-1">
                  Includes 1% protocol fee. Price increases after purchase.
                </p>
              </div>

              {txError && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
                  <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-300 font-body">{txError}</p>
                </div>
              )}

              <button
                onClick={handleBuy}
                disabled={loading || !monInput || parseFloat(monInput) <= 0}
                className="btn-pink w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <><Loader2 size={14} className="animate-spin" /> Confirming…</>
                ) : (
                  <><ShoppingCart size={14} /> Buy {token.symbol}</>
                )}
              </button>
            </>
          ) : (
            /* ── Sell Flow ── */
            <>
              {/* Balance + Price */}
              <div className="bg-white/5 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-white/50 font-body">Current Price</span>
                  <span className="font-mono text-sm text-[--color-pink] font-bold">
                    {formatMon(priceWei, 9)} MON / {token.symbol}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-white/50 font-body">Your Balance</span>
                  <div className="flex items-center gap-1.5">
                    {loadingBalance && <Loader2 size={11} className="text-white/40 animate-spin" />}
                    <span className="font-mono text-xs text-white/80">
                      {walletAddress
                        ? (loadingBalance ? "—" : `${formatTokens(userBalance)} ${token.symbol}`)
                        : "Wallet not connected"
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Token Amount Input */}
              <div className="mb-4">
                <label className="text-xs text-white/50 font-body mb-1.5 block">
                  Amount to sell ({token.symbol})
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="0.0"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    className="w-full bg-white/5 border border-[--color-border] rounded-xl px-4 py-3 font-mono text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[--color-pink]/60 transition-colors"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-white/40 font-body">
                    {token.symbol}
                  </span>
                </div>
                {/* Quick % buttons */}
                {userBalance > 0n && (
                  <div className="flex gap-2 mt-2">
                    {(["25%", "50%", "75%", "MAX"] as const).map((label, i) => {
                      const pct = [25n, 50n, 75n, 100n][i];
                      const amt = (userBalance * pct) / 100n;
                      const display = (Number(amt) / 1e18).toFixed(2);
                      return (
                        <button
                          key={label}
                          onClick={() => setTokenInput(display)}
                          className="flex-1 text-xs py-1 rounded-lg bg-white/5 border border-[--color-border] text-white/60 hover:text-[--color-pink] hover:border-[--color-pink]/50 transition-all font-mono"
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* MON Estimate */}
              <div className="bg-white/5 rounded-xl p-3 mb-5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-white/50 font-body">You receive (est.)</span>
                  <div className="flex items-center gap-1.5">
                    {estimatingSell && <Loader2 size={12} className="text-[--color-pink] animate-spin" />}
                    <span className="font-mono text-sm text-white font-semibold">
                      {estimatedMon > 0n ? formatMon(estimatedMon) : "—"}{" "}
                      <span className="text-[--color-pink]">MON</span>
                    </span>
                  </div>
                </div>
                <p className="text-[10px] text-white/30 font-body mt-1">
                  Includes 1% protocol fee. Price decreases after sale.
                </p>
              </div>

              {txError && (
                <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
                  <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-300 font-body">{txError}</p>
                </div>
              )}

              <button
                onClick={handleSell}
                disabled={loading || !tokenInput || parseFloat(tokenInput) <= 0 || !walletAddress}
                className="btn-pink w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <><Loader2 size={14} className="animate-spin" /> Confirming…</>
                ) : (
                  <><TrendingDown size={14} /> Sell {token.symbol}</>
                )}
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}